import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Big } from 'big.js';
import { NgxSpinnerService } from 'ngx-spinner';
import { finalize } from 'rxjs/operators';
import { CurrencySymbolPipe } from 'src/app/shared/pipes/currency-symbol.pipe';
import { NumberSpacingPipe } from 'src/app/shared/pipes/number-spacing.pipe';
import { ToSpanishPipe } from 'src/app/shared/pipes/to-spanish.pipe';
import { OtdInvoiceRow, OtdService } from '../../../services/otd.service';
import { OtdSaleModalComponent } from '../../organisms/otd-sale-modal/otd-sale-modal.component';

export interface OtdListRow extends OtdInvoiceRow {
  partitions?: OtdInvoiceRow[];
}

@Component({
  selector: 'app-otd-list',
  templateUrl: './otd-list.component.html',
  styleUrls: ['./otd-list.component.scss'],
})
export class OtdListComponent implements OnInit {
  invoices: OtdListRow[] = [];
  currency = 'pen';
  currencyOptions = [
    { value: 'pen', text: 'Soles (S/)' },
    { value: 'usd', text: 'Dólares (US$)' },
  ];
  searchText = '';
  selectedIds = new Set<string>();
  expandedIds = new Set<string>();
  loading = true;
  errorMessage: string | null = null;

  private currencySymbol = new CurrencySymbolPipe();
  private numberSpacing = new NumberSpacingPipe();
  private toSpanish = new ToSpanishPipe();

  constructor(private otdService: OtdService, private spinner: NgxSpinnerService, private dialog: MatDialog) {}

  ngOnInit() {
    this.spinner.show();
    this.otdService
      .getInvoices()
      .pipe(
        finalize(() => {
          this.loading = false;
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (invoices) => {
          this.invoices = this.foldPartitions(invoices);
        },
        error: () => {
          this.errorMessage = 'Algo salió mal, inténtalo en unos minutos.';
        },
      });
  }

  changeCurrency(currency: string) {
    this.currency = currency;
    this.selectedIds.clear();
  }

  search(text: string) {
    this.searchText = text;
  }

  toggle(invoice: OtdInvoiceRow) {
    if (this.selectedIds.has(invoice.id)) {
      this.selectedIds.delete(invoice.id);
    } else {
      this.selectedIds.add(invoice.id);
    }
  }

  toggleAll() {
    if (this.allListedSelected) {
      this.listedInvoices.forEach((invoice) => this.selectedIds.delete(invoice.id));
    } else {
      this.listedInvoices.forEach((invoice) => this.selectedIds.add(invoice.id));
    }
  }

  clearSelection() {
    this.selectedIds.clear();
  }

  sellToFund() {
    const invoices = this.selectedInvoices;
    const invoiceIds: string[] = [].concat(
      ...invoices.map((invoice) =>
        invoice.partitions ? invoice.partitions.map((partition) => partition.id) : [invoice.id]
      )
    );
    this.dialog
      .open(OtdSaleModalComponent, {
        width: '576px',
        panelClass: 'otd-sale-modal-panel',
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: '100%',
        position: { left: '0' },
        autoFocus: false,
        data: { invoices, invoiceIds },
      })
      .afterClosed()
      .subscribe((sold) => {
        if (sold) {
          this.removeSold(invoices);
        }
      });
  }

  get allListedSelected(): boolean {
    const listed = this.listedInvoices;
    return listed.length > 0 && listed.every((invoice) => this.selectedIds.has(invoice.id));
  }

  get selectedInvoices(): OtdListRow[] {
    return this.invoices.filter((invoice) => this.selectedIds.has(invoice.id));
  }

  get summary(): string | null {
    const selected = this.selectedInvoices;
    if (!selected.length) {
      return null;
    }

    const netTotal = selected
      .reduce((total, invoice) => total.plus(invoice.netAmount), new Big(0))
      .toFixed(2);

    const invoicesChosen = selected.length === 1 ? 'factura elegida' : 'facturas elegidas';

    return `${selected.length} ${invoicesChosen} · ${this.formatAmount(netTotal)} de monto neto`;
  }

  rowCells(invoice: OtdInvoiceRow) {
    return {
      factura: this.shortCodes(invoice.codes),
      facturaCompleta: invoice.codes.join(', '),
      pagadora: invoice.debtorName,
      girador: invoice.providerName,
      montoNeto: this.formatAmount(invoice.netAmount),
      adelanto: this.formatAmount(invoice.advanceAmount),
      vence: invoice.paymentDate,
      diasRestantes: `${invoice.remainingDays} días restantes`,
      inversionistas: invoice.investmentsCount,
    };
  }

  togglePartitions(invoice: OtdListRow) {
    if (this.expandedIds.has(invoice.id)) {
      this.expandedIds.delete(invoice.id);
    } else {
      this.expandedIds.add(invoice.id);
    }
  }

  partitionCells(partition: OtdInvoiceRow) {
    return {
      factura: partition.codes.join(', '),
      estado: this.toSpanish.transform(partition.status),
      montoNeto: this.formatAmount(partition.netAmount),
      adelanto: this.formatAmount(partition.advanceAmount),
      inversionistas: partition.investmentsCount,
    };
  }

  get emptyMessage(): string | null {
    if (this.loading || this.errorMessage || this.listedInvoices.length) {
      return null;
    }
    return 'No hay facturas listas para vender';
  }

  get listedInvoices(): OtdListRow[] {
    return this.invoices.filter(
      (invoice) => invoice.currency === this.currency && this.matchesSearch(invoice)
    );
  }

  private removeSold(soldInvoices: OtdListRow[]) {
    this.invoices = this.invoices.filter((invoice) => !soldInvoices.includes(invoice));
    soldInvoices.forEach((invoice) => this.selectedIds.delete(invoice.id));
  }

  private foldPartitions(invoices: OtdInvoiceRow[]): OtdListRow[] {
    const rows: OtdListRow[] = [];
    const mainRowsByOrigin = new Map<string, OtdListRow>();

    invoices.forEach((invoice) => {
      if (!invoice.idOrigin) {
        rows.push(invoice);
        return;
      }

      const mainRow = mainRowsByOrigin.get(invoice.idOrigin);
      if (!mainRow) {
        const newMainRow: OtdListRow = {
          ...invoice,
          id: invoice.idOrigin,
          codes: [invoice.originalCode],
          status: undefined,
          partitions: [invoice],
        };
        mainRowsByOrigin.set(invoice.idOrigin, newMainRow);
        rows.push(newMainRow);
        return;
      }

      mainRow.partitions.push(invoice);
      mainRow.netAmount = new Big(mainRow.netAmount).plus(invoice.netAmount).toFixed(2);
      mainRow.advanceAmount = new Big(mainRow.advanceAmount).plus(invoice.advanceAmount).toFixed(2);
      mainRow.investmentsCount += invoice.investmentsCount;
    });

    mainRowsByOrigin.forEach((mainRow) =>
      mainRow.partitions.sort((first, second) => first.codes[0].localeCompare(second.codes[0]))
    );

    return rows.map((row) => (row.partitions && row.partitions.length === 1 ? row.partitions[0] : row));
  }

  private shortCodes(codes: string[]): string {
    const firstTwo = codes.slice(0, 2).join(', ');
    return codes.length > 2 ? `${firstTwo}, ...` : firstTwo;
  }

  private formatAmount(amount: string): string {
    return `${this.currencySymbol.transform(this.currency)} ${this.numberSpacing.transform(amount)}`;
  }

  private matchesSearch(invoice: OtdInvoiceRow): boolean {
    const searchText = this.withoutCaseOrAccents(this.searchText);
    return (
      this.withoutCaseOrAccents(invoice.debtorName).includes(searchText) ||
      invoice.debtorRuc.includes(searchText)
    );
  }

  private withoutCaseOrAccents(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }
}
